import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import './TiptapEditor.css';

const TiptapEditor = ({
  value = '',
  onChange,
  placeholder = 'Enter text here...',
  readOnly = false,
  style = {},
  className = ''
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      Link.configure({
        openOnClick: false,
      })
    ],
    content: value || '',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (onChange) {
        // Return empty string for empty editor (similar to ReactQuill behavior)
        onChange(html === '<p></p>' ? '' : html);
      }
    },
  });

  React.useEffect(() => {
    if (!editor) return;

    // Update content when value prop changes
    const currentContent = editor.getHTML();
    if (value !== currentContent && value !== undefined) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  React.useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [readOnly, editor]);

  React.useEffect(() => {
    if (!editor) return;

    // Set placeholder
    editor.extensionManager.extensions.find(
      extension => extension.name === 'placeholder'
    );
  }, [placeholder, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={`tiptap-editor-wrapper ${className}`} style={style}>
      <div className="tiptap-toolbar">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
          disabled={readOnly}
        >
          H1
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
          disabled={readOnly}
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
          disabled={readOnly}
        >
          H3
        </button>
        <span className="toolbar-separator">|</span>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          disabled={readOnly}
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          disabled={readOnly}
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'is-active' : ''}
          disabled={readOnly}
        >
          <s>S</s>
        </button>
        <span className="toolbar-separator">|</span>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          disabled={readOnly}
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          disabled={readOnly}
        >
          1. List
        </button>
        <span className="toolbar-separator">|</span>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editor.isActive('blockquote') ? 'is-active' : ''}
          disabled={readOnly}
        >
          "
        </button>
        <button
          type="button"
          onClick={() => {
            const url = window.prompt('Enter URL:');
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          className={editor.isActive('link') ? 'is-active' : ''}
          disabled={readOnly}
        >
          Link
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive('link') || readOnly}
        >
          Unlink
        </button>
        <span className="toolbar-separator">|</span>
        <button
          type="button"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          disabled={readOnly}
        >
          Clear
        </button>
      </div>
      <EditorContent
        editor={editor}
        className="tiptap-editor-content"
        placeholder={placeholder}
      />
    </div>
  );
};

export default TiptapEditor;
